import { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useClickOutside } from '@/hooks/useClickOutside';
import { useOverlayAnimation } from '@/hooks/useOverlayAnimation';
import { usePopoverPositioning } from '@/hooks/usePopoverPositioning';
import { Button } from '@/components/ui/Button/Button';
import { OverlayIntrinsicSize } from '@/components/ui/OverlayIntrinsicSize/OverlayIntrinsicSize';
import './EditableCell.css';
import './ColumnWidthPopover.css';

const MIN_WIDTH = 50;
const MAX_WIDTH = 400;
const STEP = 10;

function parseWidth(value: string): number {
  const num = parseInt(value.replace(/px/g, ''), 10);
  return isNaN(num) ? 180 : Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, num));
}

function snapToStep(value: number): number {
  return Math.round(value / STEP) * STEP;
}

interface ColumnWidthPopoverProps {
  columnId: string;
  currentWidth: string;
  defaultWidth: string;
  onWidthChange: (columnId: string, width: string) => void;
  triggerRef: React.RefObject<HTMLElement | null>;
  isOpen: boolean;
  onClose: () => void;
}

export function ColumnWidthPopover({
  columnId,
  currentWidth,
  defaultWidth,
  onWidthChange,
  triggerRef,
  isOpen,
  onClose,
}: ColumnWidthPopoverProps) {
  const [value, setValue] = useState(() => snapToStep(parseWidth(currentWidth)));
  const popoverRef = useRef<HTMLDivElement>(null);

  const { shouldRender, contentClasses, requestClose } = useOverlayAnimation(
    isOpen,
    useCallback(() => onClose(), [onClose]),
    true
  );

  usePopoverPositioning({
    triggerRef,
    popoverRef,
    isOpen: shouldRender,
    preferredPositionY: 'bottom',
    preferredPositionX: 'left',
    offset: 8,
    zIndex: 1000,
    dependencies: [columnId],
  });

  useClickOutside(popoverRef, requestClose as (e: MouseEvent) => void, {
    enabled: shouldRender,
    capture: false,
  });

  useEffect(() => {
    if (isOpen) {
      setValue(snapToStep(parseWidth(currentWidth)));
    }
  }, [isOpen, currentWidth]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseInt(e.target.value, 10);
    if (!isNaN(v)) setValue(snapToStep(Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, v))));
  }, []);

  const handleApply = useCallback(() => {
    onWidthChange(columnId, `${value}px`);
    onClose();
  }, [columnId, value, onWidthChange, onClose]);

  const handleReset = useCallback(() => {
    const parsed = parseWidth(defaultWidth);
    const snapped = snapToStep(parsed);
    setValue(snapped);
    onWidthChange(columnId, `${snapped}px`);
  }, [columnId, defaultWidth, onWidthChange]);

  const hasAnchor = !!triggerRef.current;
  const anchorStyle: React.CSSProperties = {
    position: 'fixed',
    pointerEvents: 'auto',
    left: hasAnchor ? -9999 : 8,
    top: hasAnchor ? -9999 : 8,
  };
  const panelStyle: React.CSSProperties = {
    width: 240,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'var(--overlay-bg)',
    backdropFilter: 'var(--overlay-blur)',
    WebkitBackdropFilter: 'var(--overlay-blur)',
    border: 'var(--overlay-border)',
    borderRadius: 'var(--overlay-radius)',
    boxShadow: 'var(--overlay-shadow)',
  };

  if (!shouldRender) return null;

  return createPortal(
    <div ref={popoverRef} style={anchorStyle}>
      <OverlayIntrinsicSize>
        <div style={panelStyle} className={`column-width-popover editable-cell-popover ${contentClasses}`}>
          <div className="editable-cell-popover__header">
            Ширина: <span className="editable-cell-popover__header-accent">{value}px</span>
          </div>
          <div className="editable-cell-popover__form">
            <input
              type="range"
              className="app-range-slider"
              min={MIN_WIDTH}
              max={MAX_WIDTH}
              step={STEP}
              value={value}
              onChange={handleChange}
            />
          </div>
          <div className="editable-cell-popover__footer">
            <Button fullWidth onClick={handleApply}>
              Сохранить
            </Button>
            <Button fullWidth variant="secondary" onClick={handleReset}>
              По умолчанию
            </Button>
          </div>
        </div>
      </OverlayIntrinsicSize>
    </div>,
    document.body
  );
}
